import { useVirtualMCSettings } from '@/src/hooks/store/useVirtualMCSettings';
import { useJamEventDetails } from '@/src/store/jam-event-details.context';
import { useJamLeaderboard } from '@/src/store/jam-leaderboard.context';
import { i18nKeys } from '@/src/utils/i18n.utils';
import { ButtonDropdown, SpaceBetween, Toggle } from '@amzn/awsui-components-react';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

const VirtualMCControls = () => {
  const dataStore = useVirtualMCSettings();
  const { isPresenterMode } = useJamLeaderboard();
  const { event } = useJamEventDetails();
  const { t } = useTranslation();

  const openVideoHandler = useCallback((videoSrc) => {
    if (!dataStore.videoVisible) {
      dataStore.setPlayingVideoSrc(videoSrc);
      dataStore.setVideoVisible(true);
    } else {
      dataStore.setPlayingVideoSrc(null);
      dataStore.setVideoVisible(false);

      setTimeout(() => {
        dataStore.setPlayingVideoSrc(videoSrc);
        dataStore.setVideoVisible(true);
      }, 75);
    }
  }, []);

  const videoItems = useMemo(() => {
    const { opening, milestones, challengeCompleted, closing } = dataStore.videoSources ?? {};

    const items: { id: string; text: string; src: string }[] = [];

    if (opening) {
      items.push({ id: 'opening', text: t(i18nKeys.footer.virtualMcControls.opening), src: opening });
    }

    for (const [i, src] of (milestones ?? []).entries()) {
      if (!src) {
        continue;
      }

      items.push({
        id: `milestone-${i}`,
        text: t(i18nKeys.footer.virtualMcControls.milestone, { number: i + 1 }),
        src,
      });
    }

    if (challengeCompleted) {
      items.push({
        id: 'challengeCompleted',
        text: t(i18nKeys.footer.virtualMcControls.challengeCompleted),
        src: challengeCompleted,
      });
    }

    if (closing) {
      items.push({ id: 'closing', text: t(i18nKeys.footer.virtualMcControls.closing), src: closing });
    }

    return items;
  }, [dataStore.videoSources, t]);

  if (!isPresenterMode || !event?.facilitator || !event?.virtualMcEnabled) {
    return null;
  }

  const renderVirtualMcVideoControls = () => {
    if (!videoItems.length) {
      return null;
    }

    return (
      <>
        <Toggle
          data-testid="virtual-mc-control__video-autoplay-toggle"
          checked={dataStore.videoAutoplay}
          onChange={({ detail }) => dataStore.setVideoAutoplay(detail.checked)}
        >
          {t(i18nKeys.footer.virtualMcControls.enableVideoAutoplay)}
        </Toggle>

        <ButtonDropdown
          expandToViewport
          variant="primary"
          onItemClick={({ detail }) => {
            const videoItem = videoItems.find(({ id }) => id === detail?.id);

            if (videoItem) {
              openVideoHandler(videoItem.src);
            }
          }}
          items={videoItems.map(({ id, text }) => ({ id, text }))}
        >
          {t(i18nKeys.footer.virtualMcControls.playVideo)}
        </ButtonDropdown>
      </>
    );
  };

  return (
    <span className="awsui-polaris-dark-mode ">
      <SpaceBetween direction="horizontal" size="m" alignItems="center">
        <Toggle
          data-testid="virtual-mc-control__audio-toggle"
          checked={dataStore.audioEnabled}
          onChange={({ detail }) => dataStore.setAudioEnabled(detail.checked)}
        >
          {t(i18nKeys.footer.virtualMcControls.enableAnnouncements)}
        </Toggle>
        {renderVirtualMcVideoControls()}
      </SpaceBetween>
    </span>
  );
};

export default VirtualMCControls;
